import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import TestimonialCard from './TestimonialCard';
import CounterAnimation from './CounterAnimation';
import '../../pages/LandingPageV2.css';

const TRUSTPILOT_URL_FR = 'https://fr.trustpilot.com/review/rintintin-pro.com';
const TRUSTPILOT_URL_ES = 'https://es.trustpilot.com/review/rintintin-pro.com';

const TESTIMONIAL_KEYS = [
    'stephanieOmbredane',
    'sandrineChambraud',
    'sophieMain',
    'marionDylewski',
    'amandineTartas',
    'vanessa',
    'laNicheAventure',
    'cecileHochart',
    'marion',
    'elodieErnult',
    'nicole',
    'canipolis',
    'jennifer',
    'lucieTranchon',
    'educJoy',
    'caniCoach',
    'xanEguiguren',
] as const;

type WolItem = {
    name: string;
    role: string;
    quote: string;
    source: 'Trustpilot';
    sourceUrl: string;
};

function useCompactWol() {
    const [on, setOn] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 992px)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 992px)');
        const update = () => setOn(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);
    return on;
}

function MobileWall({
    testimonials,
    trustpilotUrl,
    ns,
}: {
    testimonials: WolItem[];
    trustpilotUrl: string;
    ns: string;
}) {
    const { t } = useTranslation('marketing');
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const count = testimonials.length;

    const syncActive = useCallback(() => {
        const root = scrollerRef.current;
        if (!root) return;
        const slides = root.querySelectorAll<HTMLElement>('.lp-wol__slide');
        if (!slides.length) return;
        const mid = root.scrollLeft + root.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
            const center = slide.offsetLeft + slide.offsetWidth / 2;
            const dist = Math.abs(center - mid);
            if (dist < bestDist) {
                bestDist = dist;
                best = i;
            }
        });
        setActive(best);
    }, []);

    const goTo = useCallback((index: number) => {
        const root = scrollerRef.current;
        const slide = root?.querySelectorAll<HTMLElement>('.lp-wol__slide')[index];
        if (!root || !slide) return;
        const left = slide.offsetLeft - (root.clientWidth - slide.offsetWidth) / 2;
        root.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }, []);

    return (
        <div className="lp-wol__mobile">
            <div
                ref={scrollerRef}
                className="lp-wol__scroller"
                onScroll={syncActive}
                role="region"
                aria-roledescription="carousel"
                aria-label={`${t(`${ns}.titlePrefix`)} ${t(`${ns}.titleHighlight`)}`}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowRight') {
                        e.preventDefault();
                        goTo(Math.min(count - 1, active + 1));
                    } else if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        goTo(Math.max(0, active - 1));
                    }
                }}
            >
                {testimonials.map((item, i) => (
                    <article
                        key={`${item.name}-${i}`}
                        className="lp-wol__slide"
                        aria-label={t(`${ns}.mobile.position`, { current: i + 1, total: count })}
                    >
                        <TestimonialCard {...item} compact quoteMaxLines={5} className="lp-wol__mobile-card" />
                    </article>
                ))}
            </div>
            <div className="lp-wol__nav">
                <button
                    type="button"
                    className="lp-wol__nav-btn"
                    aria-label={t(`${ns}.mobile.prev`)}
                    disabled={active === 0}
                    onClick={() => goTo(active - 1)}
                >
                    <ChevronLeft size={20} strokeWidth={2.4} />
                </button>
                <p className="lp-wol__nav-pos" aria-live="polite">
                    {t(`${ns}.mobile.position`, { current: active + 1, total: count })}
                </p>
                <button
                    type="button"
                    className="lp-wol__nav-btn"
                    aria-label={t(`${ns}.mobile.next`)}
                    disabled={active === count - 1}
                    onClick={() => goTo(active + 1)}
                >
                    <ChevronRight size={20} strokeWidth={2.4} />
                </button>
            </div>
            <a
                className="lp-wol__all"
                href={trustpilotUrl}
                target="_blank"
                rel="noopener noreferrer"
            >
                {t(`${ns}.mobile.allReviews`)}
            </a>
        </div>
    );
}

/**
 * Section « Wall of Love » — marquee desktop ; carrousel snap en ≤992px.
 */
export default function LandingWallOfLove() {
    const { t, i18n } = useTranslation('marketing');
    const ns = 'landingWallOfLove';
    const compact = useCompactWol();
    const trustpilotUrl = i18n.language.startsWith('es') ? TRUSTPILOT_URL_ES : TRUSTPILOT_URL_FR;

    const testimonials = useMemo(
        () =>
            TESTIMONIAL_KEYS.map((key) => ({
                name: t(`${ns}.testimonials.${key}.name`),
                role: t(`${ns}.testimonials.${key}.role`),
                quote: t(`${ns}.testimonials.${key}.quote`),
                source: 'Trustpilot' as const,
                sourceUrl: trustpilotUrl,
            })),
        [t, trustpilotUrl]
    );

    return (
        <section
            id="temoignages"
            className={compact ? 'lp-wol lp-wol--compact' : 'lp-wol'}
            style={{ padding: '160px 0', background: '#fff', overflow: 'hidden' }}
        >
            <div className="lp-wol__intro" style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px', textAlign: 'center', marginBottom: '80px' }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        background: '#f8fafc',
                        borderRadius: '100px',
                        border: '1px solid #e2e8f0',
                        color: '#0ea5e9',
                        fontSize: '13px',
                        fontWeight: 800,
                        marginBottom: '24px',
                    }}
                >
                    <div style={{ width: '8px', height: '8px', background: '#0ea5e9', borderRadius: '50%' }} />
                    {t(`${ns}.chip`)}
                </motion.div>
                <h2 className="lp-section-title">
                    {t(`${ns}.titlePrefix`)}{' '}
                    <span style={{ fontFamily: 'Plus Jakarta Sans', fontWeight: 700, color: '#A5765C' }}>
                        {t(`${ns}.titleHighlight`)}
                        <span className="lp-title-dot" aria-hidden="true" />
                    </span>
                </h2>
                <p className="lp-section-subtitle" style={{ maxWidth: '600px', margin: '0 auto' }}>
                    {t(`${ns}.subtitlePrefix`)}{' '}
                    <span style={{ fontWeight: 800, color: '#A5765C' }}>
                        <CounterAnimation end={500} suffix="+" />
                    </span>{' '}
                    {t(`${ns}.subtitleSuffix`)}
                </p>
            </div>

            {compact ? (
                <MobileWall testimonials={testimonials} trustpilotUrl={trustpilotUrl} ns={ns} />
            ) : (
            <div style={{ position: 'relative', width: '100%' }}>
                <div
                    className="lp-wol__fade lp-wol__fade--left"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        width: '200px',
                        background: 'linear-gradient(to right, white, transparent)',
                        zIndex: 10,
                        pointerEvents: 'none',
                    }}
                />
                <div
                    className="lp-wol__fade lp-wol__fade--right"
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '200px',
                        background: 'linear-gradient(to left, white, transparent)',
                        zIndex: 10,
                        pointerEvents: 'none',
                    }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                    <motion.div
                        animate={{ x: [0, -2000] }}
                        transition={{ duration: 75, repeat: Infinity, ease: 'linear' }}
                        style={{ display: 'flex', gap: '32px', width: 'max-content' }}
                    >
                        {[...testimonials, ...testimonials, ...testimonials].map((item, i) => (
                            <div key={i} className="lp-wol__card" style={{ width: '450px', flexShrink: 0 }}>
                                <TestimonialCard {...item} quoteMaxLines={6} />
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        animate={{ x: [-2000, 0] }}
                        transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
                        style={{ display: 'flex', gap: '32px', width: 'max-content' }}
                    >
                        {[...testimonials, ...testimonials, ...testimonials].map((item, i) => (
                            <div key={i} className="lp-wol__card" style={{ width: '450px', flexShrink: 0 }}>
                                <TestimonialCard {...item} quoteMaxLines={6} />
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
            )}
        </section>
    );
}
